import { Checkbox, Form, InputNumber, Radio, Select } from "antd";
import { AktPercentageInput } from "components/aktPercentageInput";
import { AktDatePicker } from "components/aktDatePicker";
import SkipTraceSubForm from "features/bulkActions/wizardSteps/skipTraceSubForm";
import { useFetchCommunicationMethodsQuery } from "features/communicationMethodsTable/communicationMethodsAPI";
import { useCommunicationMethodPermissions } from "features/communicationMethodsTable/hooks/useCommunicationMethodPermissions";
import { useFetchForwardingEntitiesQuery } from "features/forwardingEntities/forwardingEntitiesAPI";
import {
  activeCollectorsWithRolesSelector,
  useFetchAgencyWorkflowStates,
  useFetchCollectorsQuery,
} from "features/home/agencyPortal/homeAPI";
import { collectorFullName } from "features/home/utils";
import { useFetchProductOfferingsQuery } from "features/productOfferings/productOfferingsAPI";
import { useFetchCommunicationSequencesQuery } from "features/sequencesTable/communicationSequenceAPI";
import styled from "styled-components";
import { useDebtorTagPermissions } from "features/tags/hooks/useDebtorTagPermissions";
import { useFetchDebtorTagsQuery } from "features/tags/tagsAPI";
import { useFetchCreditorSummariesQuery } from "features/creditors/agencyPortal/creditorsAPI";
import { useFetchDesksQuery } from "features/desksTable/desksAPI";
import { useFetchAgencyQuery } from "features/basicInfo/agencyPortal/agencySettingsAPI";
import { useFetchAllAccountUdfCustomFieldsQuery } from "features/userDefinedFields/accountUserDefinedFieldsAPI";
import { useFetchAllDebtorUdfCustomFieldsQuery } from "features/userDefinedFields/agencyPortal/debtorUserDefinedFieldsAPI";
import UdfCustomFieldFormItem from "features/accountsTable/components/UdfCustomFieldFormItem";

const StyledHeader = styled.h4`
  font-weight: 600;
  margin-top: 0px;
  margin-bottom: 8px;
`;

const StyledCard = styled.div`
  margin-bottom: 32px;
  padding-top: 8px;
`;

const StyledSelect = styled(Select)`
  width: 200px;
  margin-right: 4px;
`;

export default function AccountOperationsStep({ setHideParentModal }) {
  const form = Form.useFormInstance();
  const { data: forwardingEntities = [], isLoading: isForwardEntitiesLoading } =
    useFetchForwardingEntitiesQuery();
  const { data: communicationSequences = [], isLoading: isCommunicationSequencesLoading } =
    useFetchCommunicationSequencesQuery({
      orderBy: "-id",
    });
  const { data: communicationMethods = [], isLoading: isCommunicationMethodsLoading } =
    useFetchCommunicationMethodsQuery();
  const { filterAllowedCommunicationMethods } = useCommunicationMethodPermissions();
  const allowedCommunicationMethods = filterAllowedCommunicationMethods(communicationMethods);
  const { data: productOfferings = [], isLoading: isProductOfferingsLoading } =
    useFetchProductOfferingsQuery();
  const { data: defaultWorkflowStates = [], isLoading: isDefaultWorkflowStatesLoading } =
    useFetchAgencyWorkflowStates();
  const { activeCollectorsWithRoles: collectors = [], isLoading: isCollectorsWithRolesLoading } =
    useFetchCollectorsQuery(undefined, {
      selectFromResult: (result) => ({
        ...result,
        activeCollectorsWithRoles: activeCollectorsWithRolesSelector(result),
      }),
    });
  const { data: creditors = [], isLoading: isCreditorsLoading } = useFetchCreditorSummariesQuery();
  const { data: debtorTags = [], isLoading: isDebtorTagsLoading } = useFetchDebtorTagsQuery();
  const { filterAddableTags, filterRemovableTags } = useDebtorTagPermissions();
  const addableDebtorTags = filterAddableTags(debtorTags);
  const removableDebtorTags = filterRemovableTags(debtorTags);
  const { data: desks = [], isLoading: isDesksLoading } = useFetchDesksQuery();
  const { data: agencySettings = {} } = useFetchAgencyQuery();
  const { data: accountCustomFields = [], isLoading: isAccountCustomFieldsLoading } =
    useFetchAllAccountUdfCustomFieldsQuery();
  const { data: debtorCustomFields = [], isLoading: isDebtorCustomFieldsLoading } =
    useFetchAllDebtorUdfCustomFieldsQuery();

  const filterOption = (inputValue, option) => {
    const fullOptionText = option.label;
    return fullOptionText?.toLowerCase().includes(inputValue?.toLowerCase());
  };

  // If delete is unchecked, clear all other fields
  const onDeleteAccountsChange = (e) => {
    const value = e.target.checked;

    if (value) {
      form.setFieldValue("operations", { delete: true });
    }
  };

  // If Avtal action is selected, clear all other operations
  const onAvtalActionChange = (value) => {
    if (value) {
      form.setFieldValue("operations", {
        sendToAvtal: { action: value },
      });
    }
  };

  // If Tratta is selected, clear all other operations
  const onTrattaExportChange = (e) => {
    const value = e.target.checked;

    if (value) {
      form.setFieldValue("operations", {
        trattaExport: true,
      });
    }
  };

  const validateUpdateDebtorTags = (_, value) => {
    const addTagIds = form.getFieldValue(["operations", "updateDebtorTags", "addTagIds"]);
    const removeTagIds = form.getFieldValue(["operations", "updateDebtorTags", "removeTagIds"]);

    if (addTagIds && removeTagIds) {
      const duplicateTags = addTagIds.filter((tagId) => removeTagIds.includes(tagId));
      if (duplicateTags.length > 0) {
        return Promise.reject(
          new Error("The same tag cannot be in both 'Add Debtor Tags' and 'Remove Debtor Tags'."),
        );
      }
    }

    return Promise.resolve();
  };

  return (
    <StyledCard>
      <StyledHeader>Apply one or more actions to the accounts in bulk: </StyledHeader>
      <Form.Item
        noStyle
        shouldUpdate={(prev, curr) =>
          prev?.operations?.delete !== curr?.operations?.delete ||
          prev?.operations?.sendToAvtal?.action !== curr?.operations?.sendToAvtal?.action ||
          prev?.operations?.trattaExport !== curr?.operations?.trattaExport
        }
      >
        {({ getFieldValue }) => {
          const deleteValue = getFieldValue(["operations", "delete"]);
          const avtalValue = getFieldValue(["operations", "sendToAvtal", "action"]);
          const trattaValue = getFieldValue(["operations", "trattaExport"]);

          const disableOtherOperations = deleteValue || avtalValue || trattaValue;

          return (
            <>
              <Form.Item valuePropName="checked" name={["operations", "delete"]}>
                <Checkbox onChange={onDeleteAccountsChange} disabled={avtalValue || trattaValue}>
                  Delete Accounts
                </Checkbox>
              </Form.Item>
              {agencySettings?.isTrattaEnabled && (
                <Form.Item valuePropName="checked" name={["operations", "trattaExport"]}>
                  <Checkbox onChange={onTrattaExportChange} disabled={deleteValue || avtalValue}>
                    Tratta Export
                  </Checkbox>
                </Form.Item>
              )}
              <Form.Item valuePropName="checked" name="skipTrace">
                <Checkbox disabled={disableOtherOperations}>Skip Trace</Checkbox>
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(prev, curr) => prev?.skipTrace !== curr?.skipTrace}>
                {({ getFieldValue: getskipTraceValue }) => {
                  const skipTrace = getskipTraceValue("skipTrace");
                  return skipTrace ? (
                    <SkipTraceSubForm
                      setHideParentModal={setHideParentModal}
                      bulkActionType="accountSkipTrace"
                    />
                  ) : null;
                }}
              </Form.Item>
              <Form.Item
                name={["operations", "changeInterestRate", "interestRate"]}
                label="Change Interest Rate"
              >
                <AktPercentageInput disabled={disableOtherOperations} />
              </Form.Item>
              <Form.Item
                name={["operations", "changeStatus", "workflowStateId"]}
                label="Change Status"
              >
                <StyledSelect
                  allowClear
                  popupMatchSelectWidth={false}
                  placeholder="Select Status..."
                  filterOption={filterOption}
                  options={defaultWorkflowStates.map((state) => ({
                    label: [state.code, state.name].join(" - "),
                    value: state.id,
                    title: state.description,
                  }))}
                  loading={isDefaultWorkflowStatesLoading}
                  disabled={disableOtherOperations}
                />
              </Form.Item>
              <Form.Item
                name={["operations", "assignToCollector", "id"]}
                label="Assign to Collector"
              >
                <StyledSelect
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  popupMatchSelectWidth={false}
                  options={[
                    {
                      label: "Unassigned",
                      value: null,
                    },
                    ...collectors.map((collector) => ({
                      label: collectorFullName(collector),
                      value: collector.id,
                    })),
                  ]}
                  placeholder="Select one..."
                  loading={isCollectorsWithRolesLoading}
                  disabled={disableOtherOperations}
                />
              </Form.Item>
              <Form.Item
                name={["operations", "assignToCollectorsEvenly", "ids"]}
                label="Evenly assign accounts to collectors"
              >
                <StyledSelect
                  allowClear
                  showSearch
                  mode="multiple"
                  filterOption={(input, option) =>
                    (option.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  popupMatchSelectWidth={false}
                  options={collectors.map((collector) => ({
                    label: collectorFullName(collector),
                    value: collector.id,
                  }))}
                  placeholder="Select collectors..."
                  loading={isCollectorsWithRolesLoading}
                  disabled={disableOtherOperations}
                />
              </Form.Item>
              <Form.Item name={["operations", "assignToDesk", "id"]} label="Assign to Desk">
                <StyledSelect
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  popupMatchSelectWidth={false}
                  options={[
                    {
                      label: "Unassigned",
                      value: null,
                    },
                    ...desks.map((desk) => ({
                      label: desk.name,
                      value: desk.id,
                    })),
                  ]}
                  placeholder="Select desk..."
                  loading={isDesksLoading}
                  disabled={disableOtherOperations}
                />
              </Form.Item>
              <Form.Item name={["operations", "updateCreditor", "id"]} label="Update Creditor">
                <StyledSelect
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  popupMatchSelectWidth={false}
                  options={creditors.map((creditor) => ({
                    label: creditor.code ? `${creditor.name} (${creditor.code})` : creditor.name,
                    value: creditor.id,
                  }))}
                  placeholder="Select one..."
                  loading={isCreditorsLoading}
                  disabled={disableOtherOperations}
                />
              </Form.Item>
              <Form.Item
                name={["operations", "assignToForwardingEntity", "id"]}
                label="Assign Forwarding Entity"
              >
                <Select
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  placeholder="Select one..."
                  popupMatchSelectWidth={false}
                  options={[
                    {
                      label: "Unassigned",
                      value: null,
                    },
                    ...forwardingEntities.map((forwardingEntity) => ({
                      label: forwardingEntity.name,
                      value: forwardingEntity.id,
                    })),
                  ]}
                  loading={isForwardEntitiesLoading}
                  disabled={disableOtherOperations}
                />
              </Form.Item>
              <Form.Item
                name={["operations", "sendAiDialerCampaign", "communicationMethodId"]}
                label="Schedule AI Phone Agent Campaign"
                tooltip="The calls are scheduled and may not be sent immediately."
              >
                <StyledSelect
                  allowClear
                  placeholder="Select one.."
                  options={allowedCommunicationMethods
                    .filter(
                      (communicationMethod) =>
                        communicationMethod.medium.includes(
                          "artificial_intelligence_phone_agent",
                        ) && !communicationMethod.isArchived,
                    )
                    .map((each) => ({
                      label: each.useMultipleAccountsPerTask
                        ? `${each.name} (Multiple Accounts)`
                        : each.name,
                      value: each.id,
                    }))}
                  disabled={disableOtherOperations}
                />
              </Form.Item>
              <Form.Item
                name={["operations", "sendLetter", "communicationMethodId"]}
                label="Schedule Letter"
                tooltip="The letters are scheduled and may not be sent immediately."
              >
                <StyledSelect
                  allowClear
                  placeholder="Select one.."
                  options={allowedCommunicationMethods
                    .filter(
                      (communicationMethod) =>
                        communicationMethod.medium.includes("letter") &&
                        !communicationMethod.isArchived,
                    )
                    .map((each) => ({
                      label: each.useMultipleAccountsPerTask
                        ? `${each.name} (Multiple Accounts)`
                        : each.name,
                      value: each.id,
                    }))}
                  disabled={disableOtherOperations}
                />
              </Form.Item>
              <Form.Item
                name={["operations", "sendText", "communicationMethodId"]}
                label="Schedule SMS"
                tooltip="The texts are scheduled and may not be sent immediately."
              >
                <StyledSelect
                  allowClear
                  placeholder="Select one.."
                  showSearch
                  filterOption={filterOption}
                  options={allowedCommunicationMethods
                    .filter(
                      (communicationMethod) =>
                        communicationMethod.medium === "text" && !communicationMethod.isArchived,
                    )
                    .map((each) => ({
                      label: each.name,
                      value: each.id,
                    }))}
                  disabled={disableOtherOperations}
                  loading={isCommunicationMethodsLoading}
                />
              </Form.Item>
              <Form.Item
                name={["operations", "sendEmail", "communicationMethodId"]}
                label="Schedule Email"
                tooltip="The emails are scheduled and may not be sent immediately."
              >
                <StyledSelect
                  allowClear
                  placeholder="Select one.."
                  showSearch
                  filterOption={filterOption}
                  options={allowedCommunicationMethods
                    .filter(
                      (communicationMethod) =>
                        communicationMethod.medium === "email" && !communicationMethod.isArchived,
                    )
                    .map((each) => ({
                      label: each.name,
                      value: each.id,
                    }))}
                  disabled={disableOtherOperations}
                  loading={isCommunicationMethodsLoading}
                />
              </Form.Item>
              <Form.Item
                name={["operations", "sendVoicemail", "communicationMethodId"]}
                label="Schedule Voicemail"
                tooltip="The voicemails are scheduled and may not be sent immediately."
              >
                <StyledSelect
                  allowClear
                  placeholder="Select one.."
                  showSearch
                  filterOption={filterOption}
                  options={allowedCommunicationMethods
                    .filter(
                      (communicationMethod) =>
                        communicationMethod.medium === "voicemail" &&
                        !communicationMethod.isArchived,
                    )
                    .map((each) => ({
                      label: each.name,
                      value: each.id,
                    }))}
                  disabled={disableOtherOperations}
                  loading={isCommunicationMethodsLoading}
                />
              </Form.Item>
              <Form.Item
                name={["operations", "assignToCommunicationSequence", "id"]}
                label="Assign Communication Sequence"
              >
                <StyledSelect
                  allowClear
                  placeholder="Select one.."
                  options={communicationSequences.map((each) => ({
                    label: each.name,
                    value: each.id,
                  }))}
                  loading={isCommunicationSequencesLoading}
                  disabled={disableOtherOperations}
                />
              </Form.Item>
              <Form.Item
                name={["operations", "changeProductOffering", "productOfferingId"]}
                label="Change Product Offering"
              >
                <StyledSelect
                  allowClear
                  placeholder="Select one..."
                  options={productOfferings.map((each) => ({
                    label: each.name,
                    value: each.id,
                  }))}
                  loading={isProductOfferingsLoading}
                  disabled={disableOtherOperations}
                />
              </Form.Item>
              <Form.Item
                name={["operations", "updateDebtorTags", "addTagIds"]}
                label="Add Debtor Tags"
                rules={[{ validator: validateUpdateDebtorTags }]}
              >
                <StyledSelect
                  allowClear
                  showSearch
                  mode="multiple"
                  filterOption={(input, option) =>
                    (option.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  popupMatchSelectWidth={false}
                  options={addableDebtorTags.map((tag) => ({
                    label: tag.name,
                    value: tag.id,
                  }))}
                  placeholder="Select tags..."
                  loading={isDebtorTagsLoading}
                  disabled={disableOtherOperations}
                />
              </Form.Item>
              <Form.Item
                name={["operations", "updateDebtorTags", "removeTagIds"]}
                label="Remove Debtor Tags"
                rules={[{ validator: validateUpdateDebtorTags }]}
              >
                <StyledSelect
                  allowClear
                  showSearch
                  mode="multiple"
                  filterOption={(input, option) =>
                    (option.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  popupMatchSelectWidth={false}
                  options={removableDebtorTags.map((tag) => ({
                    label: tag.name,
                    value: tag.id,
                  }))}
                  placeholder="Select tags..."
                  loading={isDebtorTagsLoading}
                  disabled={disableOtherOperations}
                />
              </Form.Item>
              <Form.Item
                name={["operations", "setTurnoverDate", "turnoverDate"]}
                label="Set Turnover Date"
              >
                <AktDatePicker disabled={disableOtherOperations} format="YYYY-MM-DD" />
              </Form.Item>
              <Form.Item name={["operations", "setOpenDate", "openDate"]} label="Set Open Date">
                <AktDatePicker disabled={disableOtherOperations} format="YYYY-MM-DD" />
              </Form.Item>
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue: getFollowUpFieldValue, setFieldValue }) => {
                  const followUpDate = getFollowUpFieldValue([
                    "operations",
                    "setFollowUpDate",
                    "followUpDate",
                  ]);
                  const followUpDays = getFollowUpFieldValue([
                    "operations",
                    "followUpDaysFromToday",
                  ]);
                  return (
                    <>
                      <Form.Item
                        name={["operations", "setFollowUpDate", "followUpDate"]}
                        label="Set Follow Up Date"
                      >
                        <AktDatePicker
                          disabled={disableOtherOperations || !!followUpDays}
                          format="YYYY-MM-DD"
                          onChange={() => {
                            setFieldValue(["operations", "followUpDaysFromToday"], undefined);
                          }}
                        />
                      </Form.Item>
                      <Form.Item
                        name={["operations", "followUpDaysFromToday"]}
                        label="# Days from today to follow up"
                      >
                        <InputNumber min={1} disabled={disableOtherOperations || !!followUpDate} />
                      </Form.Item>
                    </>
                  );
                }}
              </Form.Item>
              <Form.Item
                name={["operations", "suppressFromCreditReporting", "isSkipCreditReporting"]}
                label="Suppress from Credit Reporting?"
                tooltip={
                  <p>If "Yes" is selected, the account will be suppressed from Credit Reporting.</p>
                }
              >
                <Radio.Group disabled={disableOtherOperations}>
                  <Radio value>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item
                name={["operations", "deleteFromCreditReporting", "isDeleteFromCreditReport"]}
                label="Delete from Credit Reporting?"
                tooltip={
                  <p>
                    If "Yes" is selected, the account which may have been previously reported will
                    be credit reported as a 'DA' to signify a 'Delete Account'.
                  </p>
                }
              >
                <Radio.Group disabled={disableOtherOperations}>
                  <Radio value>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                name={["operations", "sendToAvtal", "action"]}
                label="Send to Avtal"
                tooltip="Select the action to perform on accounts in Avtal"
              >
                <StyledSelect
                  allowClear
                  placeholder="Select action..."
                  options={[
                    { label: "Place Accounts", value: "placement" },
                    { label: "Pause Accounts", value: "pause" },
                    { label: "Cancel Accounts", value: "cancel" },
                  ]}
                  disabled={!agencySettings?.isAvtalEnabled || deleteValue || trattaValue}
                  onChange={onAvtalActionChange}
                />
              </Form.Item>

              <>
                <p>Update Account Custom Fields</p>
                <Form.Item name={["operations", "updateAccountCustomFields", "customFieldValues"]}>
                  <UdfCustomFieldFormItem
                    customFieldsData={accountCustomFields}
                    placeholder="Add account custom field"
                    isLoading={isAccountCustomFieldsLoading}
                    disabled={disableOtherOperations}
                  />
                </Form.Item>
              </>

              <>
                <p>Update Debtor Custom Fields</p>
                <Form.Item name={["operations", "updateDebtorCustomFields", "customFieldValues"]}>
                  <UdfCustomFieldFormItem
                    customFieldsData={debtorCustomFields}
                    placeholder="Add debtor custom field"
                    isLoading={isDebtorCustomFieldsLoading}
                    disabled={disableOtherOperations}
                  />
                </Form.Item>
              </>
            </>
          );
        }}
      </Form.Item>
    </StyledCard>
  );
}
